import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { barsApi } from '@/api/endpoints/bars';
import { BarCard, BarFilterModal, type BarFilters } from '@/components/domain';
import { SEOHead } from '@/components/seo';
import { PageSpinner, Button, LocationPermissionPrompt, LocationErrorPrompt, NoAreaCoverage } from '@/components/ui';
import { useFilters } from '@/contexts/FilterContext';
import { getLocationSEO } from '@/lib/seoUtils';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useAreaCoverage } from '@/hooks/useAreaCoverage';

// Map URL filter values to BarFilters keys
const filterMap: Record<string, keyof BarFilters> = {
  brunch: 'servesBrunch',
  sports: 'goodForWatchingSports',
  patio: 'outdoorSeating',
  dogs: 'allowsDogs',
};

export function BarsPage() {
  const { lat, lng, radius, hasValidLocation, locationStatus, locationLabel, locationSource } = useFilters();
  const { hidden } = useUserPreferences();
  const { hasBarCoverage, isCheckingCoverage } = useAreaCoverage();
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter');

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [barFilters, setBarFilters] = useState<BarFilters>(() => {
    if (urlFilter && filterMap[urlFilter]) {
      return { [filterMap[urlFilter]]: true };
    }
    return {};
  });

  // Update filters when URL parameter changes (e.g., clicking quick filter while on page)
  useEffect(() => {
    if (urlFilter && filterMap[urlFilter]) {
      setBarFilters({ [filterMap[urlFilter]]: true });
    }
  }, [urlFilter]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['bars', { lat, lng, radius }],
    queryFn: ({ pageParam = 0 }) =>
      barsApi.list({
        lat: lat!,
        lng: lng!,
        radius,
        limit: 20,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.offset + lastPage.limit < lastPage.total
        ? lastPage.offset + lastPage.limit
        : undefined,
    initialPageParam: 0,
    enabled: hasValidLocation,
  });

  const allBars = data?.pages.flatMap((page) => page.bars) ?? [];

  // Filter out hidden bars first, then apply client-side filters
  const filteredBars = useMemo(() => {
    // First filter out hidden bars
    const visibleBars = allBars.filter((bar) => !hidden.has(bar.id));

    // Then apply attribute filters
    const activeFilters = Object.entries(barFilters).filter(([, value]) => value);
    if (activeFilters.length === 0) return visibleBars;

    return visibleBars.filter((bar) =>
      activeFilters.every(([key]) => bar[key as keyof typeof bar])
    );
  }, [allBars, barFilters, hidden]);

  const activeFilterCount = Object.values(barFilters).filter(Boolean).length;

  // Location guards
  if (!hasValidLocation) {
    if (locationStatus === 'denied') return <LocationPermissionPrompt />;
    if (locationStatus === 'unavailable') return <LocationErrorPrompt />;
    return <PageSpinner />;
  }

  if (isCheckingCoverage || isLoading) return <PageSpinner />;
  if (!hasBarCoverage) return <NoAreaCoverage />;

  if (isError) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--color-muted)]">Failed to load bars</p>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        {...getLocationSEO(
          'Browse Bars Near You',
          'Explore local bars with specials, happy hours, and events near you. Find your next favorite spot.',
          '/bars',
          locationLabel,
          locationSource
        )}
      />
      <div className="space-y-4">
        {/* Filter button */}
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setIsFilterModalOpen(true)}
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
        </Button>
      </div>

      {/* Bars list */}
      {filteredBars.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-[var(--color-muted)]">
            {activeFilterCount > 0 ? 'No bars match your filters' : 'No bars found in this area'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredBars.map((bar) => (
            <BarCard key={bar.id} bar={bar} />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasNextPage && filteredBars.length > 0 && (
        <div className="flex justify-center pt-4">
          <Button
            variant="ghost"
            onClick={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
          >
            Load More
          </Button>
        </div>
      )}

      {/* Filter modal */}
      <BarFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={barFilters}
        onApply={setBarFilters}
      />
      </div>
    </>
  );
}
